<script setup lang="ts">
import { useBookingRepository, type PublicBookingProfile } from '~/repositories/bookingRepository'

// Accepted for backwards compatibility; theming now comes from --site-accent.
defineProps<{
  primaryColor?: string | null
}>()

const { t } = useI18n()
const repo = useBookingRepository()

const { data, pending } = await useAsyncData(
  'public-bookable-staff',
  async () => {
    try {
      const res = await repo.publicListStaff()
      return res.data
    } catch {
      return []
    }
  },
  { default: () => [] as PublicBookingProfile[] }
)

const staff = computed(() => data.value ?? [])

const formatPrice = (p: PublicBookingProfile) => {
  if (!p.main_price) return t('site.book_free')
  return `${p.main_price} ${p.currency ?? ''}`.trim()
}
</script>

<template>
  <section
    v-if="staff.length || pending"
    id="book"
    class="border-y border-[var(--border)] bg-[var(--bg2)]"
  >
    <div class="mx-auto max-w-6xl px-6 py-20">
      <div class="text-center">
        <h2 class="site-display text-3xl tracking-tight">
          {{ t('site.book_title') }}
        </h2>
        <p class="mt-3 text-base ui-text-subtle">
          {{ t('site.book_subtitle') }}
        </p>
      </div>

      <div
        v-if="pending && !staff.length"
        class="mt-10 flex justify-center"
      >
        <UIcon
          name="i-lucide-loader-circle"
          class="size-6 animate-spin ui-text-subtle"
        />
      </div>

      <div
        v-else
        class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        <NuxtLink
          v-for="p in staff"
          :key="p.id"
          :to="`/book/${p.slug}`"
          class="group rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-5 transition-shadow hover:shadow-md"
        >
          <div class="flex items-start gap-4">
            <img
              v-if="p.avatar"
              :src="p.avatar"
              :alt="p.display_name"
              class="size-14 rounded-full object-cover"
            >
            <div
              v-else
              class="flex size-14 items-center justify-center rounded-full bg-[var(--bg2)] ui-text-subtle"
            >
              <UIcon
                name="i-lucide-user"
                class="size-6"
              />
            </div>
            <div class="min-w-0 flex-1">
              <h3 class="font-semibold truncate">{{ p.display_name }}</h3>
              <p
                v-if="p.title"
                class="text-sm ui-text-subtle truncate"
              >{{ p.title }}</p>
            </div>
          </div>

          <p
            v-if="p.bio"
            class="mt-4 text-sm ui-text-subtle line-clamp-3"
          >{{ p.bio }}</p>

          <div
            v-if="p.expertise?.length"
            class="mt-3 flex flex-wrap gap-1"
          >
            <span
              v-for="tag in p.expertise.slice(0, 4)"
              :key="tag"
              class="rounded-full px-2 py-0.5 text-xs bg-[var(--bg2)] ui-text-subtle"
            >
              {{ tag }}
            </span>
          </div>

          <div class="mt-4 flex items-center justify-between border-t border-[var(--border)] pt-3 text-sm">
            <span class="ui-text-subtle">{{ p.session_duration }} {{ t('site.book_min') }}</span>
            <span
              v-if="p.rating_avg !== null"
              class="flex items-center gap-1 ui-text-subtle"
            >
              <UIcon
                name="i-lucide-star"
                class="size-3.5 text-warning"
              />
              {{ p.rating_avg.toFixed(1) }} <span class="opacity-60">({{ p.ratings_count }})</span>
            </span>
            <span class="site-text-accent font-semibold">
              {{ formatPrice(p) }}
            </span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
